import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  Address,
  AddressQuery,
  AddressService,
  AddressType,
  AuthService,
  CardTokenQuery,
  Country,
  ErrorStateService,
  GiftCodeModel,
  LoggingService,
  MemberDetails,
  MemberDetailsRegistration,
  MemberQuery,
  PaymentService,
  ProviderActionKind,
  ProviderUiActionResponse,
  RegistrationBasketQuery,
  RegistrationBasketService,
  RegistrationPurchaseBasket,
  RegistrationPurchaseBasketItem,
  RegistrationStepQuery,
  RegistrationStepService,
  StepType,
} from '@fgb/core';
import { FGBValidators } from '@fgb/portal-component-library/src/lib/shared/validators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subscription, firstValueFrom } from 'rxjs';
import { tap } from 'rxjs/operators';
@Component({
  selector: 'fgbcl-registration-payment',
  templateUrl: './registration-payment.component.html',
  styleUrls: ['./registration-payment.component.scss'],
})
export class RegistrationPaymentComponent implements OnInit, OnDestroy {
  readonly stepType = StepType.Payment;
  basket$: Observable<RegistrationPurchaseBasket>;
  basketItems: RegistrationPurchaseBasketItem[] = [];
  providerResponse?: ProviderUiActionResponse;
  editingBillingAddress: boolean = false;
  editingShippingAddress: boolean = false;
  haSubmittedBillingAddress: boolean = false;
  paymentInitialised: boolean = false;
  billingAddress: Address | undefined;
  shippingAddress: Address;
  billingAddressForm: UntypedFormGroup;
  shippingAddressForm: UntypedFormGroup;
  giftCodeInput: string = '';
  totalBasketPrice: number;
  totalItemPrice: number;
  totalTaxPrice: number;
  totalShippingPrice: number;
  appliedGiftCodes: GiftCodeModel[] = [];
  giftCodeInputMade = false;
  emptyBasket: RegistrationPurchaseBasket = {
    Tax: 0,
    TotalPrice: 0,
    TotalPriceWithTaxAndShipping: 0,
    TotalPriceWithTax: 0,
    TotalShipping: 0,
    TotalTax: 0,
    GiftCodes: [],
    BasketItems: [],
  };
  currentUser: MemberDetails | undefined;
  addresses: Address[] = [];
  memberDetailsSubscription: Subscription;
  cardTokenCount: number = 0;
  payWithCardToken: boolean = false;
  giftCodeAccepted: boolean = false;
  regions: any[];

  @ViewChild('paymentModal', { static: true }) paymentModalRef: ElementRef;
  @ViewChild('unappliedGiftCodeModal', { static: true }) unappliedGiftCodeModalRef: ElementRef;
  postCodeRegex = /^\d{5}$/;

  constructor(
    private errorService: ErrorStateService,
    private registrationStepQuery: RegistrationStepQuery,
    private registrationStepService: RegistrationStepService,
    private registrationBasketService: RegistrationBasketService,
    private registrationBasketQuery: RegistrationBasketQuery,
    private logService: LoggingService,
    private paymentService: PaymentService,
    private formBuilder: UntypedFormBuilder,
    private memberQuery: MemberQuery,
    private addressQuery: AddressQuery,
    private addressService: AddressService,
    private cardTokenQuery: CardTokenQuery,
    private authService: AuthService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    // Initialize forms
    this._initializeForms();

    this.logService.info('Retrieving latest basket information...');
    this.registrationBasketService.fetchRegistrationBasketForCurrentWizard().toPromise();
    this.basket$ = this.registrationBasketQuery.select().pipe(
      tap((basket: any) => {
        if (this.appliedGiftCodes.length < 1) {
          this.basketItems = basket.BasketItems;
          this.totalBasketPrice = basket.TotalPriceWithTaxAndShipping;
          this.totalItemPrice = basket.TotalPrice;
          this.totalShippingPrice = basket.TotalShipping;
          this.totalTaxPrice = basket.TotalTax;
        }
      })
    ) as Observable<RegistrationPurchaseBasket>;
    this.basket$.pipe(tap((value) => console.log('data2: ', value))).subscribe();
    this.memberDetailsSubscription = this.memberQuery.selectMemberDetails().subscribe((md) => {
      this.currentUser = md;
      if (this.currentUser) {
        this.addressQuery
          .selectAddresses([AddressType.DefaultAddress])
          .pipe(
            tap((addresses) => {
              this.addresses = addresses;
            })
          )
          .toPromise();
      }
      this.billingAddress = this._getBillingAddress();
      // re-initialize forms with data
      this._initializeForms(this.billingAddress);
    });

    if (this.authService.isAuthenticated()) {
      this.cardTokenQuery
        .selectCard()
        .pipe(tap((tokens) => (this.cardTokenCount = tokens.length)))
        .toPromise();
    }
  }

  ngOnDestroy() {
    if (this.memberDetailsSubscription) {
      this.memberDetailsSubscription.unsubscribe();
    }
  }

  back(): void {
    this.registrationStepService.navigateToPreviousStep(this.stepType);
  }

  openPaymentModal(template: any): Promise<any> {
    return this.modalService.open(template, {
      centered: true,
      backdrop: 'static',
      windowClass: 'registration-payment-modal',
    }).result;
  }

  closeModal(): void {
    this.modalService.dismissAll();
  }

  submitStep() {
    this.errorService.clearErrors();
    this.billingAddressForm.markAllAsTouched();

    if (!this.billingAddressForm.valid) {
      this.errorService.addError(`Please update your ${!this.billingAddressForm.valid ? 'billing' : ''} address`);
      return;
    }

    if (this.billingAddressForm.valid) {
      if (!this.paymentInitialised && this.billingAddress) {
        this.paymentInitialised = true;
        this.logService.info('Retrieving provider action response for payment request...');
        const callbackUrl: string = document.getElementsByTagName('base')[0].href + '/register/payment/result';

        // Contact the API to get the provider action response
        firstValueFrom(
          this.registrationStepService.makeRegistrationPayment(
            this.billingAddress,
            this.shippingAddress,
            callbackUrl,
            this.appliedGiftCodes,
            this.payWithCardToken
          )
        )
          .then((response) => {
            if (response.ProviderActionInformation.ActionKind == ProviderActionKind.None) {
              this.registrationStepService.submitRegistrationStepData(this.stepType, this.emptyBasket).toPromise();
            } else {
              this.openPaymentModal(this.paymentModalRef).catch(() => (this.paymentInitialised = false));
              this.paymentService.process(response, 'paymentdiv', 'register/payment');
            }
          })
          .catch((error) => {
            this.logService.error(error);
            this.errorService.addError('An error occurred, please try again.');
            this.paymentInitialised = false;
          });
      }
    }
  }

  /** Gets the regions of the country */
  onCountryChange(event: Country): void {
    this.regions = event?.Regions;
  }

  private _initializeForms(billing = {}): void {
    this.billingAddressForm = this._generateBillingAddressForm(billing);
  }

  private _generateBillingAddressForm(billing: any): UntypedFormGroup {
    return this.formBuilder.group({
      // FirstName: [ billing.FirstName || '', [ Validators.required, FGBValidators.Empty ]],
      // Surname: [ billing.Surname || '', [ Validators.required, FGBValidators.Empty ]],
      Street: [billing.Street || '', [Validators.required, FGBValidators.Empty]],
      Town: [billing.Town || '', [Validators.required, FGBValidators.Empty]],
      County: [billing.County || '', [Validators.required, FGBValidators.Empty]],
      Postcode: [billing.Postcode || '', [Validators.required, Validators.pattern(this.postCodeRegex), FGBValidators.Empty]],
      Country: [billing.Country || '', [Validators.required, FGBValidators.Empty]],
      AddressType: [AddressType.BillingAddress1],
    });
  }

  /** Start editing the user's billing address. */
  editBillingAddress() {
    if (!this.billingAddress) {
      return;
    }
    this.editingBillingAddress = true;
    this.logService.debug('Opening billing address form with initial value', this.billingAddress);
  }

  /* Cancel editing and revert back to initial value */
  cancelBillingForm(): void {
    this.editingBillingAddress = false;
    // re-initialize form with the initial value
    this.billingAddressForm = this._generateBillingAddressForm(this.billingAddress);
  }

  /** Update the user's billing address with the value of the billingAddressForm. */
  updateBillingAddress() {
    this.billingAddressForm.markAllAsTouched();
    this.haSubmittedBillingAddress = true;
    if (this.billingAddress && this.billingAddressForm.valid) {
      this.billingAddress = { ...this.billingAddress, ...this.billingAddressForm.value };

      this.editingBillingAddress = false;

      if (this.authService.isAuthenticated()) {
        this.addressService.saveAddress(this.billingAddress!);
      }
    }
  }

  /** Clear the billing address form fields. */
  clearAddressForm(addressForm: FormGroup) {
    addressForm.patchValue({
      Street: '',
      Town: '',
      County: '',
      Postcode: '',
      Country: '',
    });
  }

  /** Gets the user's address from the personal details step data. */
  private _getDefaultBillingAddress(): Address | undefined {
    const personalDetailsStep = this.registrationStepQuery.getStep(StepType.PersonalDetails);
    if (personalDetailsStep) {
      const personalDetails = personalDetailsStep.Data as MemberDetailsRegistration;
      return {
        AddressName: 'Billing Address',
        FirstName: personalDetails.FirstName,
        Surname: personalDetails.Surname,
        Street: personalDetails.Street,
        Town: personalDetails.Town,
        County: personalDetails.County,
        Postcode: personalDetails.PostCode,
        Country: personalDetails.Country,
        HomeNumber: personalDetails.HomeNumber,
        EmailAddress: personalDetails.EmailAddress1,
        PortalId: personalDetails.PortalId,
        ClubId: personalDetails.ClubId,
      } as Address;
    }
    if (this.addresses) {
      let billingAddress1 = this.addresses.filter((a) => a.AddressType === AddressType.BillingAddress1)[0] as Address;
      if (billingAddress1) {
        return billingAddress1;
      }

      let billingAddress2 = this.addresses.filter((a) => a.AddressType === AddressType.BillingAddress2)[0] as Address;
      if (billingAddress2) {
        return billingAddress2;
      }
    }
    if (this.currentUser) {
      return {
        AddressName: 'Billing Address',
        FirstName: this.currentUser.FirstName,
        Surname: this.currentUser.Surname,
        Street: this.currentUser.Street,
        Town: this.currentUser.Town,
        County: this.currentUser.County,
        Postcode: this.currentUser.PostCode,
        Country: this.currentUser.Country,
        HomeNumber: this.currentUser.HomeNumber,
        EmailAddress: this.currentUser.EmailAddress1,
        PortalId: this.currentUser.PortalId,
        ClubId: this.currentUser.ClubId,
      } as Address;
    }
    return;
  }

  /** Gets the user's billing address from the session storage, or the personal details step if not found. */
  private _getBillingAddress(): Address | undefined {
    try {
      const billingAddressFromSession = sessionStorage.getItem('BillingAddress');
      if (billingAddressFromSession) {
        this.logService.info('Using billing address from session storage');
        return JSON.parse(billingAddressFromSession);
      }
    } catch {
      this.logService.error('Failed to retrieve billing address from session storage, using default address instead...');
    }

    return this._getDefaultBillingAddress();
  }

  resetBasketValues() {
    this.registrationBasketQuery
      .select()
      .pipe(
        tap((basket) => {
          this.totalBasketPrice = basket.TotalPriceWithTaxAndShipping;
          this.totalItemPrice = basket.TotalPrice;
          this.totalShippingPrice = basket.TotalShipping;
          this.totalTaxPrice = basket.TotalTax;
        })
      )
      .toPromise();
  }
}
